import { useEffect, useState } from 'react'

import { Cancel01Icon, Edit02Icon } from '@hugeicons/react'

import clsx from 'clsx'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { useFormContext, useController, useWatch } from 'react-hook-form'
import toastr from 'toastr'


import { useDataProviderContext } from '@/shared/contexts/DataProviderContext'

import { UserPlaceholder } from './UserPlaceholder'
import buildActiveStorageFilePayload from '@/shared/lib/file/build-active-storage-file-payload'
import { getRules } from '@/shared/ui/Form/lib/rules'



const ImageInput = ({
  name,
  control,

  valueKey = 'id',
  removable,
  accept = '.png, .jpg, .jpeg, .gif',
  mediaApiScope = 'client',
  base64AsValue,
  blankImage = '/placeholders/user.svg',
  imageFit = 'cover',
  setRoleObject,
  uploadArgs = {},

  size,
  solid,
  white,
  flush,
  shadow,
  className,
  circle,
  defaultValue = '',
  required,
  min,
  max,
  minLength,
  maxLength,
  pattern,
  validate,
  onChange
}) => {
  const {
    field: { ref, value, onChange: onChangeField, ...inputProps },
    fieldState: { invalid, isTouched, isDirty },
    formState: { errors, touchedFields, dirtyFields }
  } = useController({
    name,
    control,
    rules: getRules({
      required,
      min,
      max,
      minLength,
      maxLength,
      pattern,
      validate
    }),
    defaultValue
  })
  const { getValues, setValue } = useFormContext()
  const [imageUrl, setImageUrl] = useState(null)
  const classes = clsx(
    'relative inline-block',
    circle ? 'rounded-full overflow-hidden' : 'rounded-lg overflow-hidden',
    !imageUrl && 'bg-gray-100',
    className
  )
  const { httpClient } = useDataProviderContext()
  const [image, setImage] = useState('')
  const recordImageUrl = useWatch({
    name: `${name.replace('_id', '')}.file.url`
  })

  useEffect(() => {
    const record = getValues()
    if (_.has(record, `${name.replace('_id', '')}.file.url`))
      setImageUrl(_.get(record, `${name.replace('_id', '')}.file.url`))
  }, [name, recordImageUrl])

  const handleChange = async e => {
    setImage(e.target.value)
    let record
    let value = ''
    const imageFile =
      e.target.files && e.target.files.length ? e.target.files[0] : null
    if (imageFile) {
      const filePayload = await buildActiveStorageFilePayload(imageFile)

      value = base64AsValue ? filePayload.data.split(',').pop() : filePayload

      try {
        const res = await httpClient.post(
          `${mediaApiScope ? '/' + mediaApiScope : ''}/medias`,
          {
            record: {
              file: value,
              ...(uploadArgs || {})
            }
          }
        )

        record = _.get(res, 'data.record')
        value = _.get(record, valueKey, record?.id)

        if (setRoleObject) setValue(name.replace('_id', ''), record)
      } catch (error) {
        toastr.error(
          'Ocorreu um erro inesperado ao fazer o upload da mídia, tente novamente mais tarde',
          'Ooops'
        )

        value = null
      }

      const blobUrl = URL.createObjectURL(imageFile)
      setImageUrl(blobUrl)
    } else setImageUrl(null)

    onChangeField(value)
    onChange && onChange(value, record)
  }

  const handleRemoveImage = () => {
    setImage('')
    setImageUrl(null)
    onChangeField('')
    onChange && onChange('')
  }

  const handleCancelImage = () => handleRemoveImage()

  return (
    <div className={classes}>
      <div
        className={clsx(
          'w-full h-full bg-center bg-no-repeat',
          imageFit === 'cover' ? 'bg-cover' : 'bg-contain'
        )}
        style={{
          backgroundImage: `url('${imageUrl ? imageUrl : blankImage}')`
        }}
      >
        {!imageUrl && <UserPlaceholder className="w-full" />}
      </div>
      <label
        className="absolute bottom-0 right-0 flex items-center justify-center w-6 h-6 rounded-full bg-white shadow cursor-pointer translate-x-1/4 translate-y-1/4"
        title="Alterar imagem"
        htmlFor={name.replace('_id', '')}
      >
        <Edit02Icon variant="duotone" size={14} className="text-muted-foreground" />

        <input
          id={name.replace('_id', '')}
          type="file"
          accept={accept}
          onChange={handleChange}
          value={image}
          ref={ref}
          className="hidden"
        />

        {removable && <input type="hidden" name={`remove_${name}`} />}
      </label>
      {imageUrl && (
        <button
          type="button"
          className="absolute top-0 right-0 flex items-center justify-center w-6 h-6 rounded-full bg-white shadow cursor-pointer translate-x-1/4 -translate-y-1/4 border-0"
          title="Cancelar imagem"
          onClick={handleCancelImage}
        >
          <Cancel01Icon variant="duotone" size={12} className="text-muted-foreground" />
        </button>
      )}
      {(image || imageUrl) && (
        <button
          type="button"
          className="absolute top-0 left-0 flex items-center justify-center w-6 h-6 rounded-full bg-white shadow cursor-pointer -translate-x-1/4 -translate-y-1/4 border-0"
          title="Remover imagem"
          onClick={handleRemoveImage}
        >
          <Cancel01Icon variant="duotone" size={12} className="text-muted-foreground" />
        </button>
      )}
    </div>
  )
}

const ImageInputContainer = ({ name, ...rest }) => {
  const { control } = useFormContext()

  return <ImageInput name={name} control={control} {...rest} />
}

ImageInputContainer.propTypes = {
  name: PropTypes.string.isRequired
}

export { ImageInputContainer as ImageInput }
export default ImageInputContainer
